/**
 * Who has a chat, as the console reads it.
 *
 * ant keeps assignment as a label: anyone with write access on the session
 * may reply, note, tag or close it, whoever it is assigned to. The console
 * keeps a person's chat with them (a soft lock, not a permission): on a chat
 * another agent has, the reply to the rider waits behind Take over and End
 * conversation is not offered, while internal notes, tags, priority, tickets
 * and Reassign stay (helping a colleague, or a lead moving work, is not
 * taking the chat). A chat nobody has is claimed by the first reply sent on
 * it. The assistant's sessions have their own Take over and are neither.
 */

import { Conversation } from '../../hooks/useChat';

type Assignment = Pick<Conversation, 'status' | 'handled_by' | 'assigned_admin_id'>;

/** Open and answered by a person: the chats assignment means anything on. */
const openWithAPerson = (conv: Assignment): boolean =>
  conv.status !== 'closed' && conv.handled_by !== 'assistant';

/**
 * Another agent has this open chat. Unknown while the signed-in admin's ant
 * id is (it is read once per page): the console cannot tell whose it is, and
 * leaves it as it was before the lock rather than lock the agent's own chats.
 */
export const isHeldByOther = (conv: Assignment, myAdminId: string | null): boolean =>
  Boolean(myAdminId) &&
  openWithAPerson(conv) &&
  Boolean(conv.assigned_admin_id) &&
  conv.assigned_admin_id !== myAdminId;

/** An open chat a person answers and nobody has: the first reply claims it. */
export const isUnclaimed = (conv: Assignment): boolean =>
  openWithAPerson(conv) && !conv.assigned_admin_id;

/** Who the composer says has the chat; ant may not name them. */
export const holderLabel = (name: string | null | undefined): string =>
  name?.trim() || 'Another agent';
